import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
} from '@mui/material';
import { PointOfSale, EuroSymbol } from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc } from '../services/firestoreCompat';
import { db, HAS_VALID_FIREBASE_CONFIG } from '../services/firebaseConfig';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Opening Cash dialog — fraud prevention #7 (cash reconciliation)
 *
 * Shown once at the start of a shift. Cashier counts the drawer's starting cash
 * and enters it. The value feeds the variance calc at shift close:
 *     expected_cash = opening_cash + cash_sales - refunds
 *     variance      = actual_closing_cash - expected_cash
 *
 * The dialog auto-shows on first Dashboard mount if the current shift still has
 * `cash_opening === 0` (the default seeded by signIn). Can be skipped — in that
 * case opening cash stays 0 and the shift's variance is computed off that.
 */
const OpenShiftDialog: React.FC = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [shiftId, setShiftId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Only prompt once per login session — set a local flag so a re-render
    // doesn't keep reopening the dialog.
    if (localStorage.getItem('saheeh_pos_opening_cash_asked') === '1') return;

    const id = localStorage.getItem('saheeh_pos_shift_id');
    if (!id) return;
    setShiftId(id);

    // Skip the prompt if opening cash is already set (e.g. admin seeded it).
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'pos_shifts', id));
        if (!snap.exists()) return;
        const s = snap.data();
        if (s.end_time) return; // shift already closed
        if ((s.cash_opening || 0) > 0) return;
        setOpen(true);
      } catch { /* silent */ }
    })();
  }, []);

  const handleSave = async () => {
    if (!shiftId) { setError('No active shift'); return; }
    const n = parseFloat(amount.replace(',', '.'));
    if (isNaN(n) || n < 0) { setError(t('open_shift.invalid_amount')); return; }
    setSaving(true); setError('');
    if (!HAS_VALID_FIREBASE_CONFIG) {
      // Buyer hasn't wired Firebase Functions — record the amount locally and move on.
      localStorage.setItem('saheeh_pos_opening_cash', String(n));
      localStorage.setItem('saheeh_pos_opening_cash_asked', '1');
      setOpen(false);
      setSaving(false);
      return;
    }
    try {
      const fn = httpsCallable(getFunctions(), 'setOpeningCash');
      await fn({ shift_id: shiftId, opening_cash: n });
      localStorage.setItem('saheeh_pos_opening_cash_asked', '1');
      setOpen(false);
    } catch (err: any) {
      setError(err?.message || t('open_shift.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('saheeh_pos_opening_cash_asked', '1');
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleSkip} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, bgcolor: '#1a1d2e', border: '2px solid #00C896' } }}>
      <DialogTitle sx={{ color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #2a2d42' }}>
        <PointOfSale sx={{ color: '#00C896', fontSize: 28 }} />
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{t('open_shift.title')}</Typography>
          <Typography sx={{ color: '#9ca3af', fontSize: '0.78rem' }}>
            {t('open_shift.subtitle')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography sx={{ color: '#cbd5e1', fontSize: '0.85rem', mb: 2, lineHeight: 1.5 }}>
          {t('open_shift.description')}
        </Typography>
        <Box sx={{ position: 'relative', mb: 1 }}>
          <EuroSymbol sx={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#00C896', fontSize: 24 }} />
          <input
            type="tel"
            inputMode="decimal"
            value={amount}
            onChange={(e) => { setAmount(e.target.value); setError(''); }}
            placeholder="0.00"
            autoFocus
            style={{
              width: '100%', padding: '14px 16px 14px 44px', borderRadius: 10,
              border: `2px solid ${error ? '#ef5350' : '#3a3d52'}`,
              background: '#1e2130', color: '#fff',
              fontSize: '1.5rem', fontWeight: 700, outline: 'none', boxSizing: 'border-box',
            }}
          />
        </Box>
        {error && <Typography sx={{ color: '#ef5350', fontSize: '0.8rem' }}>{error}</Typography>}
        <Typography sx={{ color: '#6b7280', fontSize: '0.7rem', mt: 2, fontStyle: 'italic' }}>
          {t('open_shift.tip')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #2a2d42', px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleSkip} disabled={saving} sx={{ color: '#9ca3af', textTransform: 'none' }}>
          {t('open_shift.skip')}
        </Button>
        <Button onClick={handleSave} disabled={saving} variant="contained"
          sx={{ bgcolor: '#00C896', color: '#1a1d2e', fontWeight: 700, textTransform: 'none',
            '&:hover': { bgcolor: '#7bc012' } }}>
          {saving ? t('open_shift.saving') : t('open_shift.start_shift')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OpenShiftDialog;
